import * as React from 'react'
import { useSelector } from 'react-redux'
import { usePermission } from 'admin/hocs'
import { getActiveAccountSlug } from 'auth/selectors'
import { useHasFeatureFunc } from 'utils/useHasFeature'
import { PageResult } from '../pageDirectory'
import { pendingReservationsSearchPath, RESERVATION_REFERENCE_PATTERN } from '../schema'
import { OrderedGroup } from './useGlobalSearch'
import { useTranslation } from 'translations'

// The federated search only indexes pay-later group bookings, so a valid
// reservation reference can come back empty (e.g. regular reservations from
// the Pending Reservations page). That page searches all reservations —
// offer it as a way out instead of a dead end.
export const useReservationReferenceFallback = (
    query: string,
    orderedGroups: OrderedGroup[],
    loading: boolean,
    enabled: boolean,
): PageResult | null => {
    const { t } = useTranslation()
    const accountSlug = useSelector(getActiveAccountSlug)
    const { hasPermission } = usePermission()
    const hasFeature = useHasFeatureFunc()

    return React.useMemo(() => {
        if (!enabled || !accountSlug || loading) {
            return null
        }
        const reference = query.match(RESERVATION_REFERENCE_PATTERN)?.[0]
        if (!reference) {
            return null
        }
        const hasGroupBookingHit = orderedGroups.some(
            ({ meta, group }) => meta.key === 'group_booking' && group.results.length > 0,
        )
        if (hasGroupBookingHit) {
            return null
        }
        // Same gate the reservations intent applies (useScopedView INTENT_GATES).
        if (!hasPermission('access_orders', accountSlug) || !hasFeature('openCartView')) {
            return null
        }
        return {
            title: t('globalSearch.reservationFallback.title'),
            subtitle: t('globalSearch.reservationFallback.subtitle', { reference }),
            iconName: 'ShoppingCart',
            path: pendingReservationsSearchPath(
                accountSlug,
                reference,
                t('globalSearch.reservationFallback.via', { reference }),
            ),
        }
    }, [enabled, accountSlug, loading, query, orderedGroups, hasPermission, hasFeature, t])
}
